import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  IconButton,
  Chip,
  Button,
  CircularProgress,
  Pagination,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonAddIcon from "@mui/icons-material/PersonAddAlt1";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { PortalService } from "services/portal";

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString("es-CL", { dateStyle: "short", timeStyle: "short" });
}

export const PortalAdminList = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const limit = 25;

  const fetchPage = useCallback(async () => {
    setLoading(true);
    try {
      const res = await PortalService.listPortalMembers({ page, limit, search });
      setItems(res.items || []);
      setTotal(res.total || 0);
    } catch (err) {
      console.error("[portal-admin] list error", err);
      setFeedback({ kind: "error", msg: "No se pudo cargar la lista" });
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const onSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleInvite = async (memberId) => {
    setBusyId(memberId);
    try {
      await PortalService.inviteMember(memberId);
      setFeedback({ kind: "ok", msg: "Invitación enviada" });
      fetchPage();
    } catch (err) {
      setFeedback({ kind: "error", msg: err?.response?.data?.error || "Error al invitar" });
    } finally {
      setBusyId(null);
    }
  };

  const handleToggle = async (memberId) => {
    setBusyId(memberId);
    try {
      await PortalService.togglePortalAccess(memberId);
      fetchPage();
    } catch (err) {
      setFeedback({ kind: "error", msg: err?.response?.data?.error || "Error al cambiar acceso" });
    } finally {
      setBusyId(null);
    }
  };

  const handleImpersonate = async (memberId) => {
    setBusyId(memberId);
    try {
      const { token } = await PortalService.impersonateMember(memberId);
      window.open(`/portal/impersonate?token=${encodeURIComponent(token)}`, "_blank", "noopener");
    } catch (err) {
      setFeedback({ kind: "error", msg: err?.response?.data?.error || "No se pudo impersonar" });
    } finally {
      setBusyId(null);
    }
  };

  // ─── Crear acceso al portal ──────────────────────────────────
  const [createDialog, setCreateDialog] = useState({ open: false, member: null });
  const [createForm, setCreateForm] = useState({ email: "", password: "", autoPassword: true });
  const [creating, setCreating] = useState(false);
  const [createResult, setCreateResult] = useState(null);

  const openCreateDialog = (member) => {
    const defaultEmail = member.email || (member.emails && member.emails[0]) || "";
    setCreateDialog({ open: true, member });
    setCreateForm({ email: defaultEmail, password: "", autoPassword: true });
    setCreateResult(null);
  };
  const closeCreateDialog = () => {
    setCreateDialog({ open: false, member: null });
    setCreateResult(null);
  };

  const handleCreateUser = async () => {
    if (!createDialog.member) return;
    setCreating(true);
    try {
      const payload = { email: createForm.email };
      if (!createForm.autoPassword && createForm.password) {
        payload.password = createForm.password;
      }
      const res = await PortalService.createPortalUser(createDialog.member._id, payload);
      setCreateResult(res);
      fetchPage();
    } catch (err) {
      setFeedback({ kind: "error", msg: err?.response?.data?.error || "No se pudo crear el acceso" });
      closeCreateDialog();
    } finally {
      setCreating(false);
    }
  };

  const copyToClipboard = async (text) => {
    try { await navigator.clipboard.writeText(text); }
    catch { /* noop */ }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>
          Portal de Clientes
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {total} clientes
        </Typography>
      </Box>

      <Box component="form" onSubmit={onSearch} sx={{ display: "flex", gap: 1, mb: 2 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Buscar por nombre, RUT o email..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: "text.disabled" }} /> }}
        />
        <Button type="submit" variant="contained">
          Buscar
        </Button>
      </Box>

      {feedback && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 1,
            bgcolor: feedback.kind === "ok" ? "success.50" : "error.50",
            border: 1,
            borderColor: feedback.kind === "ok" ? "success.light" : "error.light",
            color: feedback.kind === "ok" ? "success.dark" : "error.dark",
            fontSize: 14,
          }}
        >
          {feedback.msg}
        </Box>
      )}

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Cliente</TableCell>
                <TableCell>RUT</TableCell>
                <TableCell>Portal</TableCell>
                <TableCell>Usuarios</TableCell>
                <TableCell>Último acceso</TableCell>
                <TableCell>SII</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    Sin resultados
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                items.map((m) => (
                  <TableRow key={m._id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>{m.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{m.email || "—"}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{m.id_number}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={m.portalEnabled ? "Activo" : "Inactivo"}
                        color={m.portalEnabled ? "success" : "default"}
                        variant={m.portalEnabled ? "filled" : "outlined"}
                      />
                    </TableCell>
                    <TableCell>
                      {m.usersCount}
                      {m.usersCount > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                          ({m.verifiedUsersCount} verificados)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{fmtDate(m.lastLogin)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={m.siiConfigured ? "Configurado" : "Pendiente"}
                        color={m.siiConfigured ? "success" : "warning"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Crear acceso al portal">
                        <span>
                          <IconButton
                            size="small"
                            disabled={busyId === m._id}
                            onClick={() => openCreateDialog(m)}
                          >
                            <PersonAddIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Ver como cliente (impersonar)">
                        <span>
                          <IconButton
                            size="small"
                            disabled={busyId === m._id || !m.portalEnabled || m.usersCount === 0}
                            onClick={() => handleImpersonate(m._id)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Reenviar invitación por email">
                        <span>
                          <IconButton
                            size="small"
                            disabled={busyId === m._id}
                            onClick={() => handleInvite(m._id)}
                          >
                            <MailOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={m.portalEnabled ? "Desactivar portal" : "Activar portal"}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={busyId === m._id}
                            onClick={() => handleToggle(m._id)}
                            color={m.portalEnabled ? "success" : "default"}
                          >
                            {m.portalEnabled ? <ToggleOnIcon /> : <ToggleOffIcon />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} color="primary" />
        </Box>
      )}

      <Dialog open={createDialog.open} onClose={creating ? undefined : closeCreateDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          Crear acceso al portal
          {createDialog.member && (
            <Typography variant="caption" color="text.secondary" display="block">
              {createDialog.member.name} — {createDialog.member.id_number}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          {!createResult ? (
            <>
              <TextField
                size="small"
                label="Email del usuario"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                fullWidth
                helperText="El cliente usará este email para iniciar sesión"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={createForm.autoPassword}
                    onChange={(e) => setCreateForm({ ...createForm, autoPassword: e.target.checked })}
                  />
                }
                label="Generar contraseña automáticamente"
              />
              {!createForm.autoPassword && (
                <TextField
                  size="small"
                  type="text"
                  label="Contraseña"
                  value={createForm.password}
                  onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                  fullWidth
                  helperText="Mínimo 6 caracteres"
                />
              )}
              <Box sx={{ p: 1.5, bgcolor: "info.50", border: 1, borderColor: "info.light", borderRadius: 1, fontSize: 12, color: "info.dark" }}>
                El usuario quedará verificado y habilitado de inmediato. Comparte
                la contraseña con el cliente y pídele que la cambie en su primer login.
              </Box>
            </>
          ) : (
            <>
              <Box sx={{ p: 2, bgcolor: "success.50", border: 1, borderColor: "success.light", borderRadius: 1, color: "success.dark" }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Acceso creado</Typography>
                <Typography variant="body2">Email: <b>{createResult.portalUser.email}</b></Typography>
                {createResult.generatedPassword && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                    <Typography variant="body2">
                      Contraseña: <code style={{ background: "#fff", padding: "2px 8px", borderRadius: 4, border: "1px solid #c6f6d5" }}>{createResult.generatedPassword}</code>
                    </Typography>
                    <IconButton size="small" onClick={() => copyToClipboard(createResult.generatedPassword)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
                <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
                  {createResult.generatedPassword
                    ? "Cópiala ahora: no la mostraremos de nuevo."
                    : "El cliente usará la contraseña que ingresaste."}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {!createResult ? (
            <>
              <Button onClick={closeCreateDialog} disabled={creating}>Cancelar</Button>
              <Button onClick={handleCreateUser} disabled={creating || !createForm.email} variant="contained">
                {creating ? "Creando..." : "Crear acceso"}
              </Button>
            </>
          ) : (
            <Button onClick={closeCreateDialog} variant="contained">Listo</Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};
